import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { styled } from '@mui/material/styles';
import {
  Tabs,
  Tab,
  CardContent,
  Card,
  LinearProgress,
  Box,
  Button,
} from '@mui/material';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';

import { Passenger } from '@/data/models/passenger.model';
import { ServicePlan } from '@/data/models/service-plan.model';
import {
  clearCurrentPassengerReservations,
  fetchPassengerFutureReservationsAsync,
  fetchPassengerPastReservationsAsync,
  loadMorePassengerFutureReservationsAsync,
  loadMorePassengerPastReservationsAsync,
  selectCurrentPassengerFutureReservations,
  selectCurrentPassengerFutureReservationsNextPageData,
  selectCurrentPassengerPastReservations,
  selectCurrentPassengerPastReservationsNextPageData,
  selectLoadingCurrentPassengerFutureReservations,
  selectLoadingCurrentPassengerPastReservations,
} from '@/data/passenger.slice';

import TabPanel from '../../TabPanel';
import ReservationList from './ReservationList';
import { AppDispatch } from '@/data/store';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { selectReservationEventEmitted } from '@/data/reservation.slice';
import {
  selectTripLegEventEmitted,
  setTripLegEventEmitted,
} from '@/data/tripleg.slice';
import { TransportReservationRequest } from '@/data/models/transport-reservation.model';
import {
  RESERVATION_REFETCH_DEBOUNCE_MS,
  RESERVATION_REFETCH_MAX_WAIT_MS,
} from '@/data/constants';

const PREFIX = 'PassengerReservation';

const classes = {
  container: `${PREFIX}-container`,
  loadMoreContainer: `${PREFIX}-loadMoreContainer`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: '100%',
  },
  [`& .${classes.loadMoreContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

interface PassengerReservationProps {
  passengerUUID: string;
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
  loadingPassenger: boolean;
  portalClientBC?: string;
}

export default function PassengerReservations({
  passengerUUID,
  passenger,
  servicePlans,
  loadingPassenger,
  portalClientBC,
}: PassengerReservationProps) {
  const dispatch: AppDispatch = useAppDispatch();
  const t = useCommonTranslation();

  const [tabIndex, setTabIndex] = React.useState(0);

  const reservationEventDetail = useAppSelector(selectReservationEventEmitted);
  const tripLegEventDetail = useAppSelector(selectTripLegEventEmitted);
  const pastReservationsLoading = useAppSelector(
    selectLoadingCurrentPassengerPastReservations,
  );
  const pastReservations = useAppSelector(
    selectCurrentPassengerPastReservations,
  );
  const pastReservationsNextPageData = useAppSelector(
    selectCurrentPassengerPastReservationsNextPageData,
  );
  const futureReservationsLoading = useAppSelector(
    selectLoadingCurrentPassengerFutureReservations,
  );
  const futureReservations = useAppSelector(
    selectCurrentPassengerFutureReservations,
  );
  const futureReservationsNextPageData = useAppSelector(
    selectCurrentPassengerFutureReservationsNextPageData,
  );

  const commonFetchParams = useMemo(() => {
    if (!passengerUUID) return null;

    return {
      passengerUUIDs: [passengerUUID],
      includeTripLegs: true,
      sort: 'reservationServiceDate',
      pageSize: 100,
      clientBC: portalClientBC,
    } as Partial<TransportReservationRequest>;
  }, [passengerUUID, portalClientBC]);

  const fetchPassengerFutureReservations = useCallback(() => {
    if (!commonFetchParams) return;
    dispatch(
      fetchPassengerFutureReservationsAsync({
        ...commonFetchParams,
        reservationState: 'active,draft',
      }),
    );
  }, [commonFetchParams]);

  const fetchPassengerPastReservations = useCallback(() => {
    if (!commonFetchParams) return;
    dispatch(
      fetchPassengerPastReservationsAsync({
        ...commonFetchParams,
        reservationState: 'inactive',
      }),
    );
  }, [commonFetchParams]);

  // A full STO cancel makes the backend emit a long cascade of trip-leg /
  // reservation events (cancel + re-open + re-inventory ...) spread over a
  // minute or more. Each event previously triggered an immediate future+past
  // refetch, producing 100+ requests for a single cancel. Debounce those
  // refetches: settle 1s after events stop, but `maxWait` guarantees the list
  // still refreshes at least every 5s during a sustained cascade so it never
  // looks frozen.
  const debouncedRefetchReservations = useMemo(
    () =>
      debounce(
        () => {
          fetchPassengerFutureReservations();
          fetchPassengerPastReservations();
        },
        RESERVATION_REFETCH_DEBOUNCE_MS,
        { maxWait: RESERVATION_REFETCH_MAX_WAIT_MS },
      ),
    [fetchPassengerFutureReservations, fetchPassengerPastReservations],
  );

  // Flush nothing / drop any pending refetch when the debounced fn is replaced
  // (passenger change) or the component unmounts.
  useEffect(() => {
    return () => {
      debouncedRefetchReservations.cancel();
    };
  }, [debouncedRefetchReservations]);

  const loadMoreFutureReservations = useCallback(() => {
    if (!futureReservationsNextPageData?.hasMorePages) return;
    dispatch(
      loadMorePassengerFutureReservationsAsync({
        ...commonFetchParams,
        reservationState: 'active,draft',
      }),
    );
  }, [commonFetchParams, futureReservationsNextPageData?.hasMorePages]);

  const loadMorePastReservations = useCallback(() => {
    if (!pastReservationsNextPageData?.hasMorePages) return;
    dispatch(
      loadMorePassengerPastReservationsAsync({
        ...commonFetchParams,
        reservationState: 'inactive',
      }),
    );
  }, [commonFetchParams, pastReservationsNextPageData?.hasMorePages]);

  // need to fetch both future and past reservations to repopulate
  const fetchedForUUID = useRef<string | null>(null);
  useEffect(() => {
    if (passengerUUID && fetchedForUUID.current !== passengerUUID) {
      fetchedForUUID.current = passengerUUID;
      dispatch(clearCurrentPassengerReservations());
      fetchPassengerFutureReservations();
      fetchPassengerPastReservations();
    }
  }, [passengerUUID]);

  useEffect(() => {
    if (
      passengerUUID &&
      reservationEventDetail?.detail?.passengerUUID === passengerUUID
    ) {
      debouncedRefetchReservations();
    }
  }, [reservationEventDetail, debouncedRefetchReservations]);

  useEffect(() => {
    if (
      passengerUUID &&
      tripLegEventDetail?.detail.passengerUUID === passengerUUID
    ) {
      debouncedRefetchReservations();
      dispatch(setTripLegEventEmitted(null));
    }
  }, [tripLegEventDetail]);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <StyledCard className={classes.container}>
      <CardContent>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={tabIndex}
          onChange={handleTabChange}
          variant="standard">
          <Tab label={t('passenger.upcoming')} data-testid="tab-one" />
          <Tab label={t('passenger.history')} data-testid="tab-two" />
        </Tabs>
        <TabPanel value={tabIndex} index={0}>
          {futureReservationsLoading && <LinearProgress />}
          <ReservationList
            passenger={passenger}
            reservations={futureReservations}
            servicePlans={servicePlans}
            loadingPassenger={loadingPassenger}
            data-testid="tab-panel-one"
          />
          {futureReservationsNextPageData?.hasMorePages && (
            <Box className={classes.loadMoreContainer}>
              <Button
                variant="outlined"
                color="primary"
                disabled={futureReservationsLoading}
                onClick={loadMoreFutureReservations}>
                {t('common.loadMore')}
              </Button>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          {pastReservationsLoading && <LinearProgress />}
          <ReservationList
            passenger={passenger}
            reservations={pastReservations}
            servicePlans={servicePlans}
            loadingPassenger={loadingPassenger}
            data-testid="tab-panel-two"
          />
          {pastReservationsNextPageData?.hasMorePages && (
            <Box className={classes.loadMoreContainer}>
              <Button
                variant="outlined"
                color="primary"
                disabled={pastReservationsLoading}
                onClick={loadMorePastReservations}>
                {t('common.loadMore')}
              </Button>
            </Box>
          )}
        </TabPanel>
      </CardContent>
    </StyledCard>
  );
}
